import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardActions,
  Button,
  Paper,
  IconButton,
  Chip,
  TextField,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  Tabs,
  Tab,
  Tooltip,
  Fab,
} from "@mui/material";
import {
  Assessment,
  Analytics,
  TrendingUp,
  TrendingDown,
  GetApp,
  Search,
  Refresh,
  Add,
  Delete,
  Visibility,
  FilterList,
  TableChart,
  People,
  AccountBalance,
  Receipt,
  Inventory,
  Description,
  PieChart,
  ShowChart,
} from "@mui/icons-material";
import { styled } from "@mui/material/styles";
import Header from "./Header";

// Constants
const DRAWER_WIDTH = 260;

// Styled components
const StyledCard = styled(Card)(({ theme }) => ({
  height: "100%",
  display: "flex",
  flexDirection: "column",
  transition: "all 0.3s ease-in-out",
  "&:hover": {
    transform: "translateY(-2px)",
    boxShadow: theme.shadows[6],
  },
}));

const MetricCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  textAlign: "center",
  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
  color: "white",
  borderRadius: theme.spacing(2),
  position: "relative",
  overflow: "hidden",
}));

const FilterSection = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  marginBottom: theme.spacing(2),
  borderRadius: theme.spacing(1),
  backgroundColor: theme.palette.background.paper,
}));

// Interfaces
interface ReportData {
  id: string;
  name: string;
  type: "financial" | "sales" | "inventory" | "users" | "performance";
  description: string;
  dateGenerated: string;
  status: "completed" | "processing" | "failed";
  size: string;
  format: "pdf" | "excel" | "csv";
}

interface ReportMetric {
  id: string;
  label: string;
  value: number | string;
  change?: number;
  changeType?: "increase" | "decrease";
  icon: React.ReactNode;
  color: string;
}

// Tab Panel Component
const TabPanel = ({
  children,
  value,
  index,
}: {
  children: React.ReactNode;
  value: number;
  index: number;
}) => (
  <div hidden={value !== index}>
    {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
  </div>
);

const Report: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState(0);
  const [reports, setReports] = useState<ReportData[]>([]);
  const [metrics, setMetrics] = useState<ReportMetric[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterType, setFilterType] = useState<string>("all");
  const [dateFrom, setDateFrom] = useState<string>("");
  const [dateTo, setDateTo] = useState<string>("");
  const [generateDialogOpen, setGenerateDialogOpen] = useState(false);
  const [selectedReportType, setSelectedReportType] = useState("");

  // Sample data
  const sampleReports: ReportData[] = [
    {
      id: "1",
      name: "Raport Vânzări Lunare",
      type: "sales",
      description: "Raport detaliat cu vânzările din ultima lună",
      dateGenerated: "2025-01-15T10:30:00Z",
      status: "completed",
      size: "2.4 MB",
      format: "pdf",
    },
    {
      id: "2",
      name: "Raport Financiar Q4",
      type: "financial",
      description: "Raport financiar pentru trimestrul 4",
      dateGenerated: "2025-01-14T14:20:00Z",
      status: "completed",
      size: "1.8 MB",
      format: "excel",
    },
    {
      id: "3",
      name: "Raport Inventar",
      type: "inventory",
      description: "Starea actuală a inventarului",
      dateGenerated: "2025-01-13T09:15:00Z",
      status: "processing",
      size: "1.2 MB",
      format: "csv",
    },
  ];

  const sampleMetrics: ReportMetric[] = [
    {
      id: "1",
      label: "Rapoarte Generate",
      value: 247,
      change: 12,
      changeType: "increase",
      icon: <Assessment />,
      color: "#1976d2",
    },
    {
      id: "2",
      label: "Vânzări Totale",
      value: "€87,450",
      change: 8.5,
      changeType: "increase",
      icon: <TrendingUp />,
      color: "#4caf50",
    },
    {
      id: "3",
      label: "Utilizatori Activi",
      value: 1248,
      change: -3,
      changeType: "decrease",
      icon: <People />,
      color: "#ff9800",
    },
  ];

  useEffect(() => {
    fetchReports();
    fetchMetrics();
  }, []);

  const fetchReports = async () => {
    setLoading(true);
    try {
      await new Promise((resolve) => setTimeout(resolve, 1000));
      setReports(sampleReports);
    } catch (err) {
      setError("Eroare la încărcarea rapoartelor");
    } finally {
      setLoading(false);
    }
  };

  const fetchMetrics = async () => {
    try {
      await new Promise((resolve) => setTimeout(resolve, 500));
      setMetrics(sampleMetrics);
    } catch (err) {
      console.error("Error fetching metrics:", err);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "completed":
        return "success";
      case "processing":
        return "warning";
      case "failed":
        return "error";
      default:
        return "default";
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case "completed":
        return "Finalizat";
      case "processing":
        return "În procesare";
      case "failed":
        return "Eșuat";
      default:
        return "Necunoscut";
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case "financial":
        return <AccountBalance />;
      case "sales":
        return <Receipt />;
      case "inventory":
        return <Inventory />;
      case "users":
        return <People />;
      case "performance":
        return <Analytics />;
      default:
        return <Description />;
    }
  };

  const getTypeText = (type: string) => {
    switch (type) {
      case "financial":
        return "Financiar";
      case "sales":
        return "Vânzări";
      case "inventory":
        return "Inventar";
      case "users":
        return "Utilizatori";
      case "performance":
        return "Performanță";
      default:
        return "General";
    }
  };

  const handleDownloadReport = (reportId: string) => {
    console.log(`Downloading report: ${reportId}`);
  };

  const handleViewReport = (reportId: string) => {
    console.log(`Viewing report: ${reportId}`);
  };

  const handleDeleteReport = (reportId: string) => {
    console.log(`Deleting report: ${reportId}`);
  };

  const handleGenerateReport = () => {
    console.log(`Generating report of type: ${selectedReportType}`);
    setGenerateDialogOpen(false);
  };

  const resetFilters = () => {
    setSearchTerm("");
    setFilterType("all");
    setDateFrom("");
    setDateTo("");
  };

  const filteredReports = reports.filter((report) => {
    const matchesSearch =
      report.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      report.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = filterType === "all" || report.type === filterType;
    return matchesSearch && matchesType;
  });

  const paginatedReports = filteredReports.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage,
  );

  return (
    <Box sx={{ display: "flex" }}>
      <Header />

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          marginTop: "64px",
          paddingTop: 1,
          minHeight: "calc(100vh - 64px)",
          backgroundColor: "grey.50",
          marginLeft: `${DRAWER_WIDTH}px`,
          paddingLeft: 1,
        }}
      >
        <Box sx={{ py: 1, px: 2, width: "100%" }}>
          {/* Header Section */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
            }}
          >
            <Typography variant="h4" component="h1" sx={{ fontWeight: "bold" }}>
              Rapoarte
            </Typography>
            <Box sx={{ display: "flex", gap: 1 }}>
              <Button
                variant="outlined"
                startIcon={<Refresh />}
                onClick={fetchReports}
                disabled={loading}
              >
                Actualizează
              </Button>
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={() => setGenerateDialogOpen(true)}
              >
                Generează Raport
              </Button>
            </Box>
          </Box>

          {error && (
            <Alert
              severity="error"
              sx={{ mb: 2 }}
              onClose={() => setError(null)}
            >
              {error}
            </Alert>
          )}

          {/* Metrics Cards */}
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mb: 3 }}>
            {metrics.map((metric) => (
              <Box key={metric.id} sx={{ flex: "1 1 250px", minWidth: 250 }}>
                <MetricCard
                  sx={{
                    background: `linear-gradient(135deg, ${metric.color} 0%, ${metric.color}CC 100%)`,
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      mb: 1,
                    }}
                  >
                    {metric.icon}
                    <Typography variant="h4" sx={{ fontWeight: "bold" }}>
                      {metric.value}
                    </Typography>
                  </Box>
                  <Typography variant="subtitle1">{metric.label}</Typography>
                  {metric.change && (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        mt: 1,
                      }}
                    >
                      {metric.changeType === "increase" ? (
                        <TrendingUp sx={{ fontSize: 16, mr: 0.5 }} />
                      ) : (
                        <TrendingDown sx={{ fontSize: 16, mr: 0.5 }} />
                      )}
                      <Typography variant="body2">
                        {Math.abs(metric.change)}%
                      </Typography>
                    </Box>
                  )}
                </MetricCard>
              </Box>
            ))}
          </Box>

          {/* Tabs */}
          <Paper sx={{ mb: 2 }}>
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              variant="fullWidth"
            >
              <Tab label="Lista Rapoarte" icon={<TableChart />} />
              <Tab label="Dashboard" icon={<Analytics />} />
              <Tab label="Analiză" icon={<ShowChart />} />
            </Tabs>
          </Paper>

          {/* Tab Panels */}
          <TabPanel value={activeTab} index={0}>
            {/* Filters */}
            <FilterSection>
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 2,
                  alignItems: "center",
                }}
              >
                <Box sx={{ flex: "1 1 250px" }}>
                  <TextField
                    fullWidth
                    label="Căutare"
                    variant="outlined"
                    size="small"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <Search sx={{ mr: 1, color: "action.active" }} />
                      ),
                    }}
                  />
                </Box>
                <Box sx={{ flex: "1 1 150px" }}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Tip Raport</InputLabel>
                    <Select
                      value={filterType}
                      label="Tip Raport"
                      onChange={(e) => setFilterType(e.target.value)}
                    >
                      <MenuItem value="all">Toate</MenuItem>
                      <MenuItem value="financial">Financiar</MenuItem>
                      <MenuItem value="sales">Vânzări</MenuItem>
                      <MenuItem value="inventory">Inventar</MenuItem>
                      <MenuItem value="users">Utilizatori</MenuItem>
                      <MenuItem value="performance">Performanță</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
                <Box sx={{ display: "flex", gap: 1 }}>
                  <Button
                    variant="outlined"
                    startIcon={<FilterList />}
                    size="small"
                  >
                    Aplică Filtre
                  </Button>
                  <Button variant="text" size="small" onClick={resetFilters}>
                    Resetează
                  </Button>
                </Box>
              </Box>
            </FilterSection>

            {/* Reports Table */}
            <StyledCard>
              <CardContent sx={{ p: 0 }}>
                {loading ? (
                  <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
                    <CircularProgress />
                  </Box>
                ) : (
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Nume Raport</TableCell>
                          <TableCell>Tip</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell>Data Generării</TableCell>
                          <TableCell>Dimensiune</TableCell>
                          <TableCell>Format</TableCell>
                          <TableCell>Acțiuni</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {paginatedReports.map((report) => (
                          <TableRow key={report.id} hover>
                            <TableCell>
                              <Box>
                                <Typography
                                  variant="subtitle2"
                                  sx={{ fontWeight: "medium" }}
                                >
                                  {report.name}
                                </Typography>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  {report.description}
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Chip
                                icon={getTypeIcon(report.type)}
                                label={getTypeText(report.type)}
                                size="small"
                                variant="outlined"
                              />
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={getStatusText(report.status)}
                                size="small"
                                color={getStatusColor(report.status) as any}
                              />
                            </TableCell>
                            <TableCell>
                              {new Date(
                                report.dateGenerated,
                              ).toLocaleDateString("ro-RO")}
                            </TableCell>
                            <TableCell>{report.size}</TableCell>
                            <TableCell>
                              <Chip
                                label={report.format.toUpperCase()}
                                size="small"
                                variant="filled"
                              />
                            </TableCell>
                            <TableCell>
                              <Box sx={{ display: "flex", gap: 0.5 }}>
                                <Tooltip title="Vizualizează">
                                  <IconButton
                                    size="small"
                                    onClick={() => handleViewReport(report.id)}
                                    disabled={report.status !== "completed"}
                                  >
                                    <Visibility />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Descarcă">
                                  <IconButton
                                    size="small"
                                    onClick={() =>
                                      handleDownloadReport(report.id)
                                    }
                                    disabled={report.status !== "completed"}
                                  >
                                    <GetApp />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Șterge">
                                  <IconButton
                                    size="small"
                                    onClick={() =>
                                      handleDeleteReport(report.id)
                                    }
                                    color="error"
                                  >
                                    <Delete />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </CardContent>
              <CardActions>
                <TablePagination
                  rowsPerPageOptions={[5, 10, 25]}
                  component="div"
                  count={filteredReports.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  labelRowsPerPage="Rânduri per pagină:"
                  labelDisplayedRows={({ from, to, count }) =>
                    `${from}-${to} din ${count}`
                  }
                />
              </CardActions>
            </StyledCard>
          </TabPanel>

          <TabPanel value={activeTab} index={1}>
            <Box sx={{ mb: 3 }}>
              <StyledCard>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Dashboard
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Integrarea Power BI a fost eliminată (component nefolosit).
                  </Typography>
                </CardContent>
              </StyledCard>
            </Box>

            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
              <Box sx={{ flex: "1 1 600px" }}>
                <StyledCard>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Raport Power BI - Analiză Performanță
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Integrarea Power BI a fost eliminată (component
                      nefolosit).
                    </Typography>
                  </CardContent>
                </StyledCard>
              </Box>
              <Box sx={{ flex: "1 1 350px" }}>
                <StyledCard>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Distribuție Rapoarte
                    </Typography>
                    <Box
                      sx={{
                        height: 300,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        backgroundColor: "grey.100",
                        borderRadius: 1,
                        border: "2px dashed",
                        borderColor: "grey.300",
                      }}
                    >
                      <Box sx={{ textAlign: "center" }}>
                        <PieChart
                          sx={{ fontSize: 64, color: "grey.400", mb: 2 }}
                        />
                        <Typography variant="h6" color="text.secondary">
                          Grafic Circular
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Distribuția rapoartelor pe tipuri
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </StyledCard>
              </Box>
            </Box>
          </TabPanel>

          <TabPanel value={activeTab} index={2}>
            <Box sx={{ width: "100%" }}>
              <StyledCard>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Analiză Avansată Power BI
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Integrarea Power BI a fost eliminată (component nefolosit).
                  </Typography>
                </CardContent>
              </StyledCard>
            </Box>
          </TabPanel>
        </Box>
      </Box>

      {/* Generate Report Dialog */}
      <Dialog
        open={generateDialogOpen}
        onClose={() => setGenerateDialogOpen(false)}
      >
        <DialogTitle>Generează Raport Nou</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>Tip Raport</InputLabel>
            <Select
              value={selectedReportType}
              label="Tip Raport"
              onChange={(e) => setSelectedReportType(e.target.value)}
            >
              <MenuItem value="financial">Raport Financiar</MenuItem>
              <MenuItem value="sales">Raport Vânzări</MenuItem>
              <MenuItem value="inventory">Raport Inventar</MenuItem>
              <MenuItem value="users">Raport Utilizatori</MenuItem>
              <MenuItem value="performance">Raport Performanță</MenuItem>
            </Select>
          </FormControl>
          <Box sx={{ display: "flex", gap: 2, mt: 2 }}>
            <TextField
              label="Data de la"
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
            <TextField
              label="Data până la"
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGenerateDialogOpen(false)}>Anulează</Button>
          <Button
            onClick={handleGenerateReport}
            variant="contained"
            disabled={!selectedReportType}
          >
            Generează
          </Button>
        </DialogActions>
      </Dialog>

      {/* Floating Action Button */}
      <Fab
        color="primary"
        aria-label="add"
        sx={{
          position: "fixed",
          bottom: 16,
          right: 16,
        }}
        onClick={() => setGenerateDialogOpen(true)}
      >
        <Add />
      </Fab>
    </Box>
  );
};

export default Report;
